/**
 * Assignment rules list state machine - replaces useAssignmentRulesStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { CrmQueueAssignmentRule } from '../../../../entities/queue/model/routing-types'

export interface AssignmentRulesListContext {
  rules: CrmQueueAssignmentRule[]
  isLoading: boolean
  error: string | null
}

function sortByPriority(rules: CrmQueueAssignmentRule[]) {
  return [...rules].sort((a, b) => b.priority - a.priority)
}

export type AssignmentRulesListEvent =
  | { type: 'SET_RULES'; rules: CrmQueueAssignmentRule[] }
  | { type: 'ADD_RULE'; rule: CrmQueueAssignmentRule }
  | { type: 'UPDATE_RULE'; id: string; updates: Partial<CrmQueueAssignmentRule> }
  | { type: 'DELETE_RULE'; id: string }
  | { type: 'REORDER_RULES'; ruleIds: string[] }
  | { type: 'TOGGLE_RULE'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as AssignmentRulesListContext,
    events: {} as AssignmentRulesListEvent,
  },
})

export const assignmentRulesListMachine = machineSetup.createMachine({
  id: 'assignmentRulesList',
  context: {
    rules: [],
    isLoading: false,
    error: null,
  },
  on: {
    SET_RULES: {
      actions: assign({
        rules: ({ event }) => (event.type === 'SET_RULES' ? sortByPriority(event.rules) : []),
      }),
    },
    ADD_RULE: {
      actions: assign({
        rules: ({ context, event }) =>
          event.type === 'ADD_RULE'
            ? sortByPriority([...context.rules, event.rule])
            : context.rules,
      }),
    },
    UPDATE_RULE: {
      actions: assign({
        rules: ({ context, event }) => {
          if (event.type !== 'UPDATE_RULE') return context.rules
          const newRules = context.rules.map((r) =>
            r.id === event.id ? { ...r, ...event.updates } : r
          )
          if (event.updates.priority !== undefined) {
            return sortByPriority(newRules)
          }
          return newRules
        },
      }),
    },
    DELETE_RULE: {
      actions: assign({
        rules: ({ context, event }) =>
          event.type === 'DELETE_RULE'
            ? context.rules.filter((r) => r.id !== event.id)
            : context.rules,
      }),
    },
    REORDER_RULES: {
      actions: assign({
        rules: ({ context, event }) => {
          if (event.type !== 'REORDER_RULES') return context.rules
          const ruleMap = new Map(context.rules.map((r) => [r.id, r]))
          const rules = event.ruleIds
            .map((id, index) => {
              const rule = ruleMap.get(id)
              if (rule) {
                return { ...rule, priority: event.ruleIds.length - index }
              }
              return null
            })
            .filter((r): r is CrmQueueAssignmentRule => r !== null)
          return rules
        },
      }),
    },
    TOGGLE_RULE: {
      actions: assign({
        rules: ({ context, event }) =>
          event.type === 'TOGGLE_RULE'
            ? context.rules.map((r) => (r.id === event.id ? { ...r, isActive: !r.isActive } : r))
            : context.rules,
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    SET_ERROR: {
      actions: assign({
        error: ({ event }) => (event.type === 'SET_ERROR' ? event.error : null),
      }),
    },
    CLEAR: {
      actions: assign({ rules: [], error: null }),
    },
  },
})
